import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { API } from "../../auth/AuthContext";
import { toast } from "sonner";

export default function UserEditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    kyc_status: "pending",
    is_admin: false,
    date_of_birth: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (user) setForm({
      name: user.name || "",
      email: user.email || "",
      kyc_status: user.kyc_status || "pending",
      is_admin: !!user.is_admin,
      date_of_birth: user.date_of_birth || "",
    });
  }, [user]);
  if (!user) return null;
  const save = async () => {
    if (form.kyc_status === "approved" && !form.date_of_birth.trim()) {
      toast.error("Date of birth is required to approve KYC");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        kyc_status: form.kyc_status,
        is_admin: form.is_admin,
      };
      if (form.date_of_birth.trim()) {
        body.date_of_birth = form.date_of_birth.trim();
      }
      await axios.patch(`${API}/admin/users/${user.user_id}`, body, { withCredentials: true });
      toast.success("User updated");
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "date_of_birth_required") {
        toast.error("Date of birth is required to approve KYC");
      } else if (detail === "renter_underage") {
        toast.error("Renter must be at least 24 years old");
      } else if (detail === "invalid_date_of_birth") {
        toast.error("Enter a valid date of birth");
      } else {
        toast.error(detail || "Update failed");
      }
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="user-edit-modal">
        <DialogHeader>
          <DialogTitle className="zc-headline text-2xl flex items-center gap-2"><Pencil size={18} /> Edit user</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-[var(--zc-text-3)] block mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="zc-input h-10 px-3 w-full text-sm" data-testid="user-edit-name" />
          </div>
          <div>
            <label className="text-xs text-[var(--zc-text-3)] block mb-1">Email</label>
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="zc-input h-10 px-3 w-full text-sm" data-testid="user-edit-email" />
          </div>
          <div>
            <label className="text-xs text-[var(--zc-text-3)] block mb-1" htmlFor="user-edit-dob">Date of birth</label>
            <input
              id="user-edit-dob"
              type="date"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="zc-input h-10 px-3 w-full text-sm"
              data-testid="user-edit-dob"
            />
            <p className="zc-hint mt-1">Required when approving KYC (renters must be 24+).</p>
          </div>
          <div>
            <label className="text-xs text-[var(--zc-text-3)] block mb-1">KYC status</label>
            <select value={form.kyc_status} onChange={(e) => setForm({ ...form, kyc_status: e.target.value })} className="zc-input h-10 px-2 w-full text-sm" data-testid="user-edit-kyc">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="manual_review">Manual review</option>
              <option value="declined">Declined</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-2" data-testid="user-edit-admin-row">
            <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} data-testid="user-edit-admin" />
            Administrator
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 h-10 zc-btn-primary text-sm disabled:opacity-50" data-testid="user-edit-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
